import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

/**
 * Save Route Modal Component
 * Modal for saving current route with a custom name
 *
 * @param {boolean} show - Whether to show the modal
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSave - Callback when route is saved (name) => Promise
 * @param {number} jobCount - Number of jobs in current route
 * @param {boolean} isOptimized - Whether route is optimized
 */
const SaveRouteModal = ({ show, onClose, onSave, jobCount, isOptimized }) => {
    const [routeName, setRouteName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    if (!show) return null;

    const handleSave = async (e) => {
        e.preventDefault();

        // Validate route name
        if (!routeName.trim()) {
            setError('Please enter a route name');
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            await onSave(routeName.trim());
            // Reset form
            setRouteName('');
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to save route');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            setRouteName('');
            setError('');
            onClose();
        }
    };

    // Quick name suggestions
    const suggestions = [
        'Today',
        'Tomorrow',
        new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
        new Date().toLocaleDateString('en-US', { weekday: 'long' })
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Save className="text-green-600" size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Save Route</h2>
                            <p className="text-sm text-gray-600">
                                {jobCount} job{jobCount !== 1 ? 's' : ''} • {isOptimized ? 'Optimized' : 'Not optimized'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSaving}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Close"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSave} className="p-6 space-y-4">
                    {/* Route name input */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Route Name *
                        </label>
                        <input
                            type="text"
                            value={routeName}
                            onChange={(e) => {
                                setRouteName(e.target.value);
                                setError('');
                            }}
                            placeholder="e.g., Today, Oct 10th, Morning Route"
                            maxLength={50}
                            disabled={isSaving}
                            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                                error
                                    ? 'border-red-500 focus:border-red-600'
                                    : 'border-gray-300 focus:border-green-500'
                            }`}
                            autoFocus
                        />
                        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                    </div>

                    {/* Quick suggestions */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Quick Suggestions
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {suggestions.map((suggestion) => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    onClick={() => setRouteName(suggestion)}
                                    disabled={isSaving}
                                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Info note */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> This will save your current route with all job details.
                            You can load it later to continue from where you left off.
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSaving}
                            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving || !routeName.trim()}
                            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={20} />
                                    Save Route
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SaveRouteModal;
